import axios from 'axios';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AtomEffect, atom, useRecoilState } from 'recoil';
import qs from 'query-string';
import { useSnackbar } from 'notistack';

export type PaymentMethod = {
  slug: string;
  name: string;
  commission: number;
  has_secondary_account: boolean;
};

export type UsePaymentMethodsParams = {
  account?: string;
  query?: string;
};

export const usePaymentMethods = ({ account, query }: UsePaymentMethodsParams = {}) => {
  const queryString = qs.stringify({ account, query });
  const { data = [] } = useQuery<PaymentMethod[]>(['paymentMethods', account, query], () =>
    axios.get(`/api/v1/payment-methods/?${queryString}`).then((res) => res.data),
  );

  const methods: Record<string, PaymentMethod> = useMemo(() => {
    return data.reduce((acc: Record<string, PaymentMethod>, method) => {
      acc[method.slug] = method;

      return acc;
    }, {});
  }, [data]);

  return {
    methods,
  };
};

export type PaymentRowData = {
  id: number;
  cardNumber: string;
  bank: string;
  amount: number;
  comment: string;
  secondaryAccount: string;
  error?: Record<string, any>;
  backendId: number | null;
};

export const defaultPaymentRowData = {
  id: 0,
  cardNumber: '',
  bank: '',
  amount: 0,
  comment: '',
  secondaryAccount: '',
  backendId: null,
};

export const fillDraftFrom = (id: number) => {
  return Array.from({ length: 10 }).map((_, i) => ({
    ...defaultPaymentRowData,
    id: i + id,
  }));
};

const localStorageEffect = (key: string): AtomEffect<PaymentRowData[]> => ({ setSelf, onSet }) => {
  const savedValue = sessionStorage.getItem(key);

  if (savedValue != null) {
    try {
      setSelf(JSON.parse(savedValue));
    } catch (e) {
      // ignore
    }
  }

  onSet((newValue, _, isReset) => {
    isReset ? sessionStorage.removeItem(key) : sessionStorage.setItem(key, JSON.stringify(newValue));
  });
};

const paymentState = atom<PaymentRowData[]>({
  key: 'paymentState',
  default: fillDraftFrom(0),
  effects: [localStorageEffect('paymentState')],
});

export const usePaymentDraft = () => {
  const [draft, updateDraft] = useRecoilState(paymentState);
  const { methods } = usePaymentMethods();
  const nonEmptyPayments = draft.filter((row) => row.amount > 0);
  const totalWithCommission = nonEmptyPayments
    .reduce((sum, { amount, bank }) => sum + amount + amount * (methods[bank]?.commission / 100), 0)
    .toFixed(2);

  return {
    total: nonEmptyPayments.reduce((sum, { amount }) => sum + amount, 0),
    totalWithCommission,
    draft,
    nonEmptyPayments,
    updateDraft,
  };
};

export type CreatePaymentParams = {
  slug: string; // bank slug
  account: string; // cardNumber
  value: number; // amount
  comment?: string;
  secondary_account?: string;
};

export type CreateRegistryParams = {
  ids: number[];
};

export type CreatePaymentResponse = {
  id: number;
};

export const useCreatePayment = () => {
  const { mutateAsync: createPayment, isLoading: isPaymentCreating } = useMutation((params: CreatePaymentParams) =>
    axios.post<CreatePaymentResponse>('/api/v1/payout/', params).then((res) => res.data),
  );
  const { mutateAsync: createRegistry, isLoading } = useMutation((params: CreateRegistryParams) =>
    axios.post('/api/v1/registry/', params).then((res) => res.data),
  );
  const { updateDraft, nonEmptyPayments } = usePaymentDraft();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  return {
    createFromDraft: async () => {
      const ids = nonEmptyPayments.map((item: PaymentRowData) => item.id);
      const payments = await Promise.allSettled(
        nonEmptyPayments.map((item) => {
          if (item.backendId) {
            return Promise.resolve({ id: item.backendId });
          }

          return createPayment({
            slug: item.bank,
            account: item.cardNumber,
            secondary_account: item.secondaryAccount || undefined,
            value: item.amount,
            comment: item.comment,
          });
        }),
      );

      const succeedIds = payments
        .map((result) => (result.status === 'fulfilled' ? result.value.id : null))
        .filter((id): id is number => id != null);

      const failedPayments = payments
        .map((result) => (result.status === 'rejected' ? result.reason?.response?.data : null))
        .reduce((prev, error, idx) => {
          return { ...prev, [ids[idx]]: error };
        }, {});
      const failedIds = Object.values(failedPayments).filter((id): id is string => id != null);

      if (failedIds.length) {
        updateDraft(
          nonEmptyPayments.map((item, idx) => {
            const payment = payments[idx];

            return {
              ...item,
              error: failedPayments[item.id],
              backendId: payment.status === 'fulfilled' ? payment.value.id : null,
            };
          }),
        );
        navigate('/create');

        return;
      }

      try {
        await createRegistry({ ids: succeedIds });
      } catch (e: any) {
        const errors = e.response?.data?.non_field_errors;

        if (errors) {
          enqueueSnackbar(errors.join(', '), { variant: 'error' });
        }

        navigate('/create');

        return;
      }

      queryClient.resetQueries(['getPayments']);
      queryClient.resetQueries(['user']);
      updateDraft(fillDraftFrom(0));
      navigate('/');
    },
    isCreating: isPaymentCreating || isLoading,
  };
};

export type Rate = {
  name: string;
  price: number;
};

export type Payment = {
  id: number;
  account: string;
  method: string;
  value: string;
  comment: string | null;
  commission: number | null;
  full_value_usdt: number | null;
  status: 'new' | 'done' | 'error';
  created_at: string;
  rate: Rate | null;
};

export type PaymentsResponse = {
  results: Payment[];
  total_value: number;
  total_value_usdt: number;
  count: number;
};

export type UsePaymentsParams = {
  from: Date;
  to: Date;
  page?: number;
};

export const usePayments = ({ from, to }: UsePaymentsParams) => {
  const dateAfter = format(startOfDay(from), 'yyyy-MM-dd');
  const dateBefore = format(endOfDay(to), 'yyyy-MM-dd');
  const { data, isLoading } = useQuery<PaymentsResponse>(['getPayments', dateAfter, dateBefore], () =>
    axios.get(`/api/v1/payouts/?date_after=${dateAfter}&date_before=${dateBefore}`).then((res) => res.data),
  );

  return {
    isLoading,
    total: data?.total_value,
    totalUSDT: data?.total_value_usdt,
    payments: data?.results,
  };
};
